import React, { useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

import "./CareerMap.css";

import SimpleLine from "../../utils/simpleline";

interface CareerMapProps {
  score: number[]; // [lbi, rbi, pei, ai, hi, wci]
  highlightIndex?: number | null;
}

const LINE_COLORS = ["#000000", "#FF6384", "#FF9F40"];

const CareerMap: React.FC<CareerMapProps> = ({ score, highlightIndex }) => {
  const { t } = useTranslation("careerMap");

  // 카테고리별 섹션 구성 데이터
  // 모든 카테고리가 4개 섹션(위에서부터 5/5/6/6 포인트 = 총 22점, 21~0)으로 동일하다.
  // 점수 구간으로는 17~21 / 12~16 / 6~11 / 0~5에 해당한다.
  const CATEGORY_SETS = useMemo(
    () => [
      {
        // 뇌인지성향 (lbi)
        title: t("cognitiveStructure.title"),
        sections: [
          { label: t("cognitiveStructure.leftBrainAnalytical"), count: 5 },
          { label: t("cognitiveStructure.leftBrainExperimental"), count: 5 },
          { label: t("cognitiveStructure.rightBrainStrategic"), count: 6 },
          { label: t("cognitiveStructure.rightBrainIntuitive"), count: 6 },
        ],
      },
      {
        // 뇌인지환경 (rbi)
        title: t("cognitiveEnvironment.title"),
        sections: [
          { label: t("cognitiveEnvironment.creativeArtistic"), count: 5 },
          { label: t("cognitiveEnvironment.creativePlanning"), count: 5 },
          { label: t("cognitiveEnvironment.practicalSelective"), count: 6 },
          { label: t("cognitiveEnvironment.realityVerification"), count: 6 },
        ],
      },
      {
        // 신체활동성향 (pei)
        title: t("physicalActivity.title"),
        sections: [
          { label: t("physicalActivity.dynamic"), count: 5 },
          { label: t("physicalActivity.active"), count: 5 },
          { label: t("physicalActivity.ambivalent"), count: 6 },
          { label: t("physicalActivity.static"), count: 6 },
        ],
      },
      {
        // 주도적행동성향 (ai)
        title: t("enterprising.title"),
        sections: [
          { label: t("enterprising.challengeOriented"), count: 5 },
          { label: t("enterprising.goalOriented"), count: 5 },
          { label: t("enterprising.harmonyOriented"), count: 6 },
          { label: t("enterprising.stabilityOriented"), count: 6 },
        ],
      },
      {
        // 대인행동성향 (hi)
        title: t("sociality.title"),
        sections: [
          { label: t("sociality.inclusiveRelationship"), count: 5 },
          { label: t("sociality.relationshipCentered"), count: 5 },
          { label: t("sociality.selectiveRelationship"), count: 6 },
          { label: t("sociality.independentRelationship"), count: 6 },
        ],
      },
      {
        // 과제행동성향 (wci)
        title: t("execution.title"),
        sections: [
          { label: t("execution.perfectionist"), count: 5 },
          { label: t("execution.taskResponsible"), count: 5 },
          { label: t("execution.keyTask"), count: 6 },
          { label: t("execution.preferredTask"), count: 6 },
        ],
      },
    ],
    [t],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const linesRef = useRef<{ remove: () => void }[]>([]);

  useEffect(() => {
    linesRef.current.forEach((line) => line.remove && line.remove());
    linesRef.current = [];

    if (!score || score.length === 0) return;

    const dataSetIndex = 0;

    const pointElements = score.map((val, idx) => {
      const safeVal = Math.max(0, Math.min(21, Math.round(Number(val))));
      const id = `c${idx + 1}-${safeVal}`;
      return document.getElementById(id);
    });

    if (pointElements.some((el) => !el)) {
      console.warn("그래프 점 요소를 찾을 수 없어 선을 그릴 수 없습니다.");
      return;
    }

    const lineStyle = {
      type: "straight",
      width: 3,
      color: LINE_COLORS[dataSetIndex],
    };

    if (typeof SimpleLine === "undefined") {
      console.error("SimpleLine 라이브러리가 로드되지 않았습니다.");
      return;
    }

    for (let i = 0; i < pointElements.length; i++) {
      const el = pointElements[i] as HTMLElement;
      el.classList.add("active");

      if (i < pointElements.length - 1) {
        const nextEl = pointElements[i + 1] as HTMLElement;
        try {
          const myLine = new SimpleLine(el, nextEl, lineStyle);
          linesRef.current.push(myLine);
        } catch (e) {
          console.error(e);
        }
      }
    }

    return () => {
      linesRef.current.forEach((line) => line.remove && line.remove());
      pointElements.forEach((el) => {
        if (el) el.classList.remove("active");
      });
    };
  }, [score, CATEGORY_SETS]); // CATEGORY_SETS가 바뀔 때도 재조정

  return (
    <div className="career-map-container" ref={containerRef}>
      <div className="map-tables-wrapper">
        <table className="map-tables">
          <tbody>
            <tr className="tables">
              {CATEGORY_SETS.map((cat, catIdx) => (
                <td key={catIdx}>
                  <div
                    className={`map-table cat-${catIdx + 1} ${
                      highlightIndex === catIdx ? "highlight" : ""
                    }`}
                  >
                    {renderCategorySections(cat.sections, catIdx + 1)}
                  </div>
                </td>
              ))}
            </tr>
            <tr className="titles">
              {CATEGORY_SETS.map((cat, catIdx) => (
                <td key={catIdx}>
                  <div className={`map-title cat-${catIdx + 1}`}>
                    {cat.title}
                  </div>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const renderCategorySections = (
  sections: { label: string; count: number }[],
  catNum: number,
) => {
  let currentPoint = 21;

  return sections.map((section, secIdx) => {
    const points = [];
    for (let i = 0; i < section.count; i++) {
      points.push(
        <div
          key={currentPoint}
          className="map-point"
          id={`c${catNum}-${currentPoint}`}
        />,
      );
      currentPoint--;
    }

    return (
      <div
        key={secIdx}
        className="section-row"
        style={{ flex: section.count, minHeight: 0 }}
      >
        <div className="section-label">{section.label}</div>
        <div className="map-points">{points}</div>
      </div>
    );
  });
};

export default CareerMap;
